import {
  type CityTypeCriteria,
  type Country,
  type CountryCriteria,
  type DateRange,
  type DefaultCriteriaIndex,
  type DepartureCity,
  type NightsRange,
  DepartureCityType,
} from '@/shared/model';
import type { ApiCountryPosition, ApiDepartureCity, ApiTourCriteria } from './types';

export function adaptCountries(positions: ApiCountryPosition[]): Country[] {
  return positions
    .map((pos) => positionToCountry(pos))
    .filter((country) => Number.isFinite(country.id) && country.name !== '');
}

function positionToCountry(pos: ApiCountryPosition): Country {
  const raw = pos.country ?? {};
  const visaDays = Number(raw.visaDays ?? 0);
  return {
    id: Number(raw.id),
    name: String(raw.name ?? ''),
    isPopular: !!pos.popular,
    position: pos.position ?? null,
    visaRequired: visaDays > 0 && !!raw.visaRequired,
    visaOnline: visaDays > 0 && !!raw.visaOnline,
    visaDays: visaDays > 0 ? visaDays : null,
    minPrice: null,
  };
}

export function adaptDepartureCities(raw: ApiDepartureCity[]): DepartureCity[] {
  return raw
    .map((city) => {
      const departureCity: DepartureCity = { id: Number(city.id), name: String(city.name ?? '') };
      if (city.type === DepartureCityType.Capital || city.type === DepartureCityType.Region) {
        departureCity.type = city.type as DepartureCityType;
      }
      if (city.countriesWithDirectFlights) {
        departureCity.countriesWithDirectFlights = city.countriesWithDirectFlights;
      }
      return departureCity;
    })
    .filter((city) => Number.isFinite(city.id) && city.name !== '');
}

function mapCriteria(raw: ApiTourCriteria | undefined): CityTypeCriteria | undefined {
  if (!raw) {
    return undefined;
  }

  const result: CityTypeCriteria = {};

  const nf = Number(raw.nightRange?.from);
  const nt = Number(raw.nightRange?.to);
  if (Number.isFinite(nf) && Number.isFinite(nt)) {
    result.nights = { from: nf, to: nt } as NightsRange;
  }

  const dFrom = raw.checkInDateRange?.from;
  const dTo = raw.checkInDateRange?.to;
  if (typeof dFrom === 'string' && dFrom !== '' && typeof dTo === 'string' && dTo !== '') {
    const flex = Number(raw.checkInDateRange?.daysFlexible);
    result.dateRange = { from: dFrom, to: dTo, daysFlexible: Number.isFinite(flex) ? flex : 0 } as DateRange;
  }

  return result.nights || result.dateRange ? result : undefined;
}

export function buildDefaultCriteriaIndex(positions: ApiCountryPosition[]): DefaultCriteriaIndex {
  const index: DefaultCriteriaIndex = {};

  for (const pos of positions) {
    const id = Number(pos.country?.id);
    if (!Number.isFinite(id)) {
      continue;
    }

    const capital = mapCriteria(pos.capitalTourCriteria);
    const region = mapCriteria(pos.regionTourCriteria);
    if (!capital && !region) {
      continue;
    }

    const entry: CountryCriteria = {};
    if (capital) {
      entry.capital = capital;
    }
    if (region) {
      entry.region = region;
    }
    index[id] = entry;
  }

  return index;
}
